import type { WcPlayer, WcPosId } from './types';
import {
  FORMATION, POS_LABEL, POS_PLURAL, SQUAD_COMP, WC_BENCH, WC_BUDGET_CAP,
  WC_COUNTRY_LIMIT, WC_POSITIONS, WC_STARTERS,
} from './domain';

export interface WcDraft {
  starters: number[];
  bench: number[];
  captainId: number | null;
  viceCaptainId: number | null;
}

export type WcValidation = { ok: true } | { ok: false; reason: string };

export function positionCounts(ids: number[], byId: Map<number, WcPlayer>): Record<WcPosId, number> {
  const counts: Record<WcPosId, number> = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
  for (const id of ids) {
    const p = byId.get(id);
    if (p) counts[p.position] += 1;
  }
  return counts;
}

export function countryCounts(ids: number[], byId: Map<number, WcPlayer>): Map<number, number> {
  const m = new Map<number, number>();
  for (const id of ids) {
    const p = byId.get(id);
    if (p) m.set(p.teamId, (m.get(p.teamId) ?? 0) + 1);
  }
  return m;
}

export function squadCost(ids: number[], byId: Map<number, WcPlayer>): number {
  let sum = 0;
  for (const id of ids) sum += byId.get(id)?.budget ?? 0;
  return Math.round(sum * 10) / 10;
}

/** Enforce the WC squad rules: 11 starters + 4 subs, fixed 2-5-5-3 composition, a
 *  valid starting formation, ≤3 per country, within budget, and a distinct
 *  captain + vice that both start. `budgetCap` defaults to the base £100m but the
 *  caller passes the manager's usable budget — max(£100m, current squad value) —
 *  so rising owned players free up extra spend (see WC_VALUATION.md, Layer 2). */
export function validateWcSquad(draft: WcDraft, byId: Map<number, WcPlayer>, budgetCap: number = WC_BUDGET_CAP, countryLimit: number = WC_COUNTRY_LIMIT): WcValidation {
  const { starters, bench, captainId, viceCaptainId } = draft;
  if (starters.length !== WC_STARTERS) return { ok: false, reason: `Pick ${WC_STARTERS} starters.` };
  if (bench.length !== WC_BENCH) return { ok: false, reason: `Pick ${WC_BENCH} substitutes.` };

  const all = [...starters, ...bench];
  if (new Set(all).size !== all.length) return { ok: false, reason: 'A player is selected twice.' };
  for (const id of all) if (!byId.has(id)) return { ok: false, reason: 'Unknown player in squad.' };

  const starterCounts = positionCounts(starters, byId);
  for (const t of WC_POSITIONS) {
    const { min, max } = FORMATION[t];
    if (starterCounts[t] < min) return { ok: false, reason: `Need ${min === max ? '' : 'at least '}${min} ${min > 1 ? POS_PLURAL[t] : POS_LABEL[t].toLowerCase()} starting.` };
    if (starterCounts[t] > max) return { ok: false, reason: `Too many ${POS_PLURAL[t]} (max ${max}).` };
  }

  const squadCounts = positionCounts(all, byId);
  for (const t of WC_POSITIONS) {
    if (squadCounts[t] !== SQUAD_COMP[t]) return { ok: false, reason: `Squad needs exactly ${SQUAD_COMP[t]} ${POS_PLURAL[t]}.` };
  }

  for (const [, n] of countryCounts(all, byId)) {
    if (n > countryLimit) return { ok: false, reason: `Max ${countryLimit} players from one country.` };
  }

  const cost = squadCost(all, byId);
  if (cost > budgetCap + 1e-9) return { ok: false, reason: `Over budget by £${(cost - budgetCap).toFixed(2)}m.` };

  if (captainId == null || !starters.includes(captainId)) return { ok: false, reason: 'Captain must be a starter.' };
  if (viceCaptainId == null || !starters.includes(viceCaptainId)) return { ok: false, reason: 'Vice-captain must be a starter.' };
  if (captainId === viceCaptainId) return { ok: false, reason: 'Captain and vice must be different.' };

  return { ok: true };
}
